import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import speechService from '@/services/speechService';
import './MessageInput.css';

// Inline SVG component for better Android WebView compatibility
const SendIcon = ({ className }) => (
  <svg 
    className={className}
    width="24" 
    height="24" 
    viewBox="0 0 28 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path 
      d="M-1.0872e-06 11.5638C0.147307 11.1811 0.240489 10.7786 0.493994 10.4381C0.835887 9.97907 1.28329 9.67193 1.80607 9.45673C7.9704 6.91581 14.1347 4.3742 20.2997 1.83329C21.5913 1.30072 22.8807 0.763386 24.1757 0.237631C25.4514 -0.280632 26.7251 0.061926 27.4993 1.13727C27.9824 1.80808 28.1105 2.5579 27.907 3.35674C27.1883 6.17621 26.4675 8.99499 25.7426 11.8124C25.7097 11.9404 25.709 12.0535 25.7426 12.1822C26.4538 14.9472 27.1609 17.7135 27.8728 20.4792C28.2647 22.002 27.5679 23.3736 26.1448 23.8619C25.4357 24.105 24.7526 24.0069 24.0729 23.7256C18.3135 21.3448 12.5513 18.97 6.79056 16.5926C5.14688 15.9142 3.50662 15.2278 1.85814 14.561C0.864663 14.1592 0.215823 13.4877 0.0376825 12.4124C0.0356264 12.3995 0.0130185 12.3899 -1.01594e-06 12.379L-1.0872e-06 11.5638ZM20.0558 12.8142C18.7438 12.8142 17.431 12.8169 16.1189 12.8128C15.5578 12.8115 15.2001 12.4274 15.2508 11.8982C15.2926 11.4658 15.6462 11.1838 16.1573 11.1838C18.7636 11.1825 21.37 11.1811 23.9763 11.1879C24.1524 11.1879 24.2264 11.1443 24.2702 10.9727C24.941 8.33711 25.6186 5.70289 26.2935 3.06799C26.362 2.80103 26.386 2.53611 26.2667 2.27119C25.9954 1.66711 25.4089 1.48732 24.6745 1.79038C18.963 4.14606 13.2509 6.50106 7.53944 8.85674C5.83683 9.55888 4.13559 10.2637 2.4323 10.9652C2.12055 11.0939 1.84923 11.2594 1.72385 11.5952C1.49158 12.2156 1.7444 12.7475 2.41722 13.026C4.33703 13.8201 6.2582 14.6108 8.17868 15.4035C13.6818 17.672 19.1836 19.9419 24.6861 22.2104C24.9471 22.318 25.2102 22.4174 25.5035 22.3472C26.1571 22.192 26.4737 21.6315 26.2914 20.9178C25.6186 18.2822 24.9389 15.6486 24.2682 13.0131C24.2264 12.8496 24.1537 12.8094 23.9927 12.8101C22.6807 12.8169 21.3679 12.8135 20.0558 12.8135L20.0558 12.8142Z" 
      fill="currentColor"
    />
  </svg>
);

const MessageInput = forwardRef(({ onSendMessage, disabled, lang = 'ar' }, ref) => {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const inputRef = useRef(null);
  const shouldFocusRef = useRef(false);
  const isProcessingRef = useRef(false);

  // Expose focus method to parent components
  useImperativeHandle(ref, () => ({
    focus: () => {
      shouldFocusRef.current = true;
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }));

  // Translations based on language
  const t = {
    placeholder: lang === 'ar' ? 'ادخل استفسارك هنا ...' : 'Enter your message here...',
    listening: lang === 'ar' ? 'استمع...' : 'Listening...',
    readyToListen: lang === 'ar' ? '🎤 جاهز للاستماع...' : '🎤 Ready to listen...',
    processing: lang === 'ar' ? 'جاري المعالجة...' : 'Processing...',
    stopRecording: lang === 'ar' ? 'إيقاف التسجيل' : 'Stop recording',
    voiceRecording: lang === 'ar' ? 'التسجيل الصوتي' : 'Voice recording',
    sendMessage: lang === 'ar' ? 'إرسال الرسالة' : 'Send message',
    send: lang === 'ar' ? 'إرسال' : 'Send',
  };

  // Re-focus input when it becomes enabled after sending a message
  useEffect(() => {
    if (!disabled && shouldFocusRef.current) {
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      shouldFocusRef.current = false;
    }
  }, [disabled]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedMessage = message.trim();
    
    if (trimmedMessage && !disabled) {
      // If recording, stop it first (discard pending transcription)
      if (isRecording) {
        speechService.stopRecognition().catch(() => { /* discarded */ });
        speechService.clearAccumulatedText();
        setIsRecording(false);
        isProcessingRef.current = false;
      }
      
      onSendMessage(trimmedMessage);
      setMessage(''); // Clear input after sending
      shouldFocusRef.current = true; // Mark that we should focus when re-enabled
      // Also try to focus immediately in case input doesn't get disabled
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleMicClick = async () => {
    // Guard against rapid double-clicks while the async start/stop is in flight.
    if (isProcessingRef.current) return;

    if (isRecording) {
      console.log('🛑 User stopped recording manually');
      isProcessingRef.current = true;
      setMessage(t.processing);
      try {
        const finalText = await speechService.stopRecognition();
        speechService.clearAccumulatedText();
        setIsRecording(false);
        isProcessingRef.current = false;

        const trimmed = (finalText || '').trim();
        if (trimmed) {
          onSendMessage(trimmed);
        }
        setMessage('');
        shouldFocusRef.current = true;
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      } catch (err) {
        console.error('❌ [MessageInput] Transcription failed:', err);
        setIsRecording(false);
        isProcessingRef.current = false;
        setMessage('');
      }
      return;
    }

    console.log('🎤 Starting recording...');
    isProcessingRef.current = true;
    speechService.clearAccumulatedText();

    const result = await speechService.startRecognition(
      // onInterim - unused for non-streaming API but kept for compatibility.
      null,
      // onResult is also called from the service with the final text;
      // we additionally await stopRecognition() above to drive the UI.
      (finalText) => {
        console.log('✅ [MessageInput] Final text:', finalText);
      },
      // onError
      (error) => {
        console.error('❌ [MessageInput] Speech error:', error);
        setIsRecording(false);
        isProcessingRef.current = false;
        setMessage('');
        speechService.clearAccumulatedText();
      }
    );

    isProcessingRef.current = false;
    if (result.success) {
      setIsRecording(true);
      setMessage('');
    } else {
      setIsRecording(false);
      speechService.clearAccumulatedText();
    }
  };

  return (
    <form className="message-input" onSubmit={handleSubmit}>
      <button 
        type="button"
        className={`mic-button ${isRecording ? 'mic-recording' : ''}`}
        onClick={handleMicClick}
        disabled={disabled}
        aria-label={isRecording ? t.stopRecording : t.voiceRecording}
      >
        {isRecording ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="6" y="6" width="12" height="12" fill="currentColor" rx="2"/>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" fill="currentColor"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 19v4m-4 0h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
      </button>
      <input
        ref={inputRef}
        type="text"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={isRecording ? t.listening : t.placeholder}
        disabled={disabled}
        className={`message-input-field ${isRecording ? 'recording-active' : ''}`}
        autoComplete="off"
      />
      <button 
        type="submit" 
        disabled={!message.trim() || disabled}
        className="message-send-button"
        aria-label={t.sendMessage}
      >
        <SendIcon className="send-icon" />
      </button>
    </form>
  );
});

MessageInput.displayName = 'MessageInput';

export default MessageInput;
